import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { AppIcon } from '../contexts/AppIconContext';
import { api } from '../api';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const { login, register } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [lang, setLang]         = useState(() => localStorage.getItem('i18nextLng') || 'en');
  const changeLang = (l) => { setLang(l); i18n.changeLanguage(l); };
  const [error, setError]       = useState('');
  const [msg, setMsg]           = useState('');
  const [loading, setLoading]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setMsg('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else if (mode === 'register') {
        await register({
          email: email.trim(),
          password,
          display_name: name.trim() || undefined,
          language: lang,
        });
      } else if (mode === 'forgot') {
        await api.forgotPassword(email.trim());
        setMsg(t('auth.forgotMsg'));
      }
    } catch (err) {
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };


  const FONT = 'Manrope, sans-serif';
  const LANGS = [
    ['da','🇩🇰 Dansk'],['en','🇬🇧 English'],['de','🇩🇪 Deutsch'],
    ['sv','🇸🇪 Svenska'],['no','🇳🇴 Norsk'],['nl','🇳🇱 Nederlands'],
    ['fr','🇫🇷 Français'],['es','🇪🇸 Español'],['fi','🇫🇮 Suomi'],
  ];

  const scrollToInput = (e) => {
    setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-base)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingTop: 'max(80px, calc(10vh + var(--safe-top)))',
      paddingBottom: '50vh',
      paddingLeft: '16px', paddingRight: '16px',
      fontFamily: FONT, overflowY: 'auto', boxSizing: 'border-box',
    }}>
      {/* Top bar: tema + sprog */}
      <div style={{ position: 'fixed', top: 'calc(16px + var(--safe-top))', right: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <select value={lang} onChange={e => changeLang(e.target.value)} style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
            borderRadius: 999, padding: '6px 32px 6px 12px', fontSize: 14,
            cursor: 'pointer', fontFamily: FONT, color: 'var(--fg-2)', outline: 'none', appearance: 'none',
          }}>
            {LANGS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: 'var(--fg-4)', pointerEvents: 'none' }}>▾</span>
        </div>
        <button type="button" onClick={toggleTheme} style={{
          background: 'var(--bg-elevated)', border: 'none', borderRadius: 999,
          padding: '6px 12px', cursor: 'pointer', fontSize: 14,
          color: 'var(--fg-2)', fontFamily: FONT,
        }}>
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
      </div>

      {/* Logo */}
      <div style={{ marginBottom: 16 }}><AppIcon size={72} /></div>

      <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--fg-1)', marginBottom: 4, fontFamily: FONT }}>
        Kitchey<span style={{ color: 'var(--mint-300)' }}>.</span>
      </h1>
      <p style={{ fontSize: 14, color: 'var(--fg-3)', marginBottom: 28 }}>{t('login.tagline')}</p>

      {mode === 'forgot' && (
        <div style={{ width: '100%', maxWidth: 320, marginBottom: 16 }}>
          <button type="button" onClick={() => { setMode('login'); setError(''); setMsg(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-3)', fontSize: 14, fontFamily: FONT }}>
            {t('auth.backToLogin')}
          </button>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--fg-1)', marginTop: 10, marginBottom: 4, fontFamily: FONT }}>
            {t('auth.forgotTitle')}
          </h2>
        </div>
      )}

      {mode !== 'forgot' && (
        <div style={{
          display: 'flex', width: '100%', maxWidth: 320,
          marginBottom: 24, background: 'var(--bg-elevated)',
          borderRadius: 999, padding: 4,
        }}>
          {[['login', t('auth.login')], ['register', t('auth.register')]].map(([m, l]) => (
            <button key={m} type="button"
              onClick={() => { setMode(m); setError(''); setMsg(''); }}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 999, border: 'none',
                fontSize: 14, fontWeight: mode === m ? 700 : 500,
                fontFamily: FONT, cursor: 'pointer',
                background: mode === m ? 'var(--brand)' : 'transparent',
                color: mode === m ? '#fff' : 'var(--fg-3)',
                transition: 'all 150ms',
              }}>{l}</button>
          ))}
        </div>
      )}

      <form onSubmit={submit} style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {mode === 'register' && (
          <input value={name} onChange={e => setName(e.target.value)} onFocus={scrollToInput}
            className="ft-input" placeholder={t('auth.name')} />
        )}
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
          onFocus={scrollToInput} className="ft-input" placeholder={t('auth.email')} autoComplete="email" />
        {mode !== 'forgot' && (
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
            onFocus={scrollToInput} className="ft-input" placeholder={t('auth.password')}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'} />
        )}

        {mode === 'register' && (
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: '12px 14px' }}>
            <p style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 8, fontFamily: FONT }}>{t('login.prefs')}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={toggleTheme} style={{
                flex: 1, padding: '8px 0', borderRadius: 10, border: '1px solid var(--border-subtle)',
                fontSize: 13, cursor: 'pointer', fontFamily: FONT,
                background: 'var(--bg-card)', color: 'var(--fg-2)',
              }}>
                {theme === 'dark' ? '🌙 ' + t('login.themeDark') : '☀️ ' + t('login.themeLight')}
              </button>
              <select value={lang} onChange={e => changeLang(e.target.value)} style={{
                flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
                borderRadius: 10, padding: '8px 10px', fontSize: 13,
                cursor: 'pointer', fontFamily: FONT, color: 'var(--fg-2)', outline: 'none',
              }}>
                {LANGS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>
        )}

        {error && <p style={{ fontSize: 13, color: 'var(--status-expired)', textAlign: 'center' }}>{error}</p>}
        {msg   && <p style={{ fontSize: 13, color: 'var(--status-fresh)',   textAlign: 'center' }}>{msg}</p>}

        <button type="submit" disabled={loading} className="ft-btn-primary" style={{ marginTop: 4 }}>
          {loading ? t('common.wait') : mode === 'login' ? t('auth.login') : mode === 'register' ? t('auth.register') : t('auth.sendResetLink')}
        </button>
      </form>

      {mode === 'login' && (
        <button type="button" onClick={() => { setMode('forgot'); setError(''); setMsg(''); }}
          style={{ marginTop: 16, fontSize: 13, color: 'var(--fg-4)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT }}>
          {t('auth.forgotPassword')}
        </button>
      )}
    </div>
  );
}
